import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { cn } from "../lib/utils";
import {
  isPresetAvatarId,
  PresetAvatarSvg,
  resolveOrgUniquePreset,
} from "../icons/avatars/presetAvatars.jsx";

const AvatarPresetContext = React.createContext({
  hasPreset: false,
  setHasPreset: () => {},
  altSeed: "",
  setAltSeed: () => {},
});

const Avatar = React.forwardRef(({ className, children, ...props }, ref) => {
  const [hasPreset, setHasPreset] = React.useState(false);
  const [altSeed, setAltSeed] = React.useState("");
  const ctx = React.useMemo(
    () => ({ hasPreset, setHasPreset, altSeed, setAltSeed }),
    [hasPreset, altSeed],
  );
  return (
    <AvatarPresetContext.Provider value={ctx}>
      <AvatarPrimitive.Root
        ref={ref}
        className={cn(
          "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
          className,
        )}
        {...props}
      >
        {children}
      </AvatarPrimitive.Root>
    </AvatarPresetContext.Provider>
  );
});
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef(({ className, src, alt, ...props }, ref) => {
  const { setHasPreset, setAltSeed } = React.useContext(AvatarPresetContext);
  const isPreset = isPresetAvatarId(src);

  React.useLayoutEffect(() => {
    setHasPreset(isPreset);
    if (alt) setAltSeed(alt);
    return () => setHasPreset(false);
  }, [isPreset, alt, setHasPreset, setAltSeed]);

  if (isPreset) {
    return (
      <PresetAvatarSvg
        presetId={src}
        title={alt}
        className={cn("relative z-10 aspect-square h-full w-full", className)}
      />
    );
  }

  return (
    <AvatarPrimitive.Image
      ref={ref}
      src={src}
      alt={alt}
      className={cn("aspect-square h-full w-full", className)}
      {...props}
    />
  );
});
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef(
  ({ className, seed, orgId = "default-org", children, ...props }, ref) => {
    const { hasPreset, altSeed } = React.useContext(AvatarPresetContext);
    if (hasPreset) return null;
    const textSeed =
      seed ||
      altSeed ||
      (typeof children === "string" ? children.trim() : "") ||
      "homy-user";
    const presetId = isPresetAvatarId(textSeed)
      ? textSeed
      : resolveOrgUniquePreset(textSeed, orgId);
    return (
      <AvatarPrimitive.Fallback
        ref={ref}
        className={cn(
          "flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-muted text-muted-foreground",
          className,
        )}
        {...props}
      >
        <PresetAvatarSvg presetId={presetId} className="h-full w-full" />
      </AvatarPrimitive.Fallback>
    );
  },
);
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };


